import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportError, toError } from '../observability/error-reporter';

export interface ErrorFallbackRenderProps {
  error: Error;
  reset: () => void;
}

interface Props {
  children: ReactNode;
  fallback: (props: ErrorFallbackRenderProps) => ReactNode;
  /** Attached to the report, so a boundary around the map and one around the
   *  runs dock are distinguishable in whatever collects them. */
  context?: Readonly<Record<string, string>> | undefined;
}

interface State {
  error: Error | null;
}

/**
 * The outermost boundary, wrapping the provider stack and the router itself.
 *
 * React Router has its own `errorElement`, which catches anything thrown
 * inside a route before a React boundary would see it. This one exists for
 * what that cannot reach: a failure in a provider, or in the router's own
 * render. Both paths end at the same fallback, so the user sees one design
 * either way.
 *
 * Still a class, because `getDerivedStateFromError` and `componentDidCatch`
 * have no hook equivalent.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { error: toError(error) };
  }

  // `override` is required — tsconfig.base.json sets noImplicitOverride.
  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    reportError({
      error: toError(error),
      // @types/react types this as string | null | undefined; normalising to
      // null keeps the report shape stable and assertable.
      componentStack: info.componentStack ?? null,
      ...(this.props.context === undefined ? {} : { context: this.props.context }),
    });
  }

  private readonly reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (error !== null) {
      return this.props.fallback({ error, reset: this.reset });
    }
    return this.props.children;
  }
}
